// import React, { useState, useEffect, useRef } from 'react';
// import axios from 'axios';
// import { getHeaders } from '../utils/apiUtils';
// import { FaSearch } from 'react-icons/fa';
// import './GenericAutocomplete.css';

// const GenericAutocomplete = ({ 
//   onSelect, 
//   apiUrl, 
//   buildFilters, 
//   displayField, 
//   placeholder,
//   value,
//   dependencies = [] 
// }) => {
//   const [inputValue, setInputValue] = useState('');
//   const [suggestions, setSuggestions] = useState([]);
//   const [isOpen, setIsOpen] = useState(false);
//   const [isFocused, setIsFocused] = useState(false);
//   const justSelected = useRef(false);

//   useEffect(() => {
//     if (value) {
//       setInputValue(value);
//     }
//   }, [value]);

//   useEffect(() => {
//     const allDependenciesMet = dependencies.every(dep => dep);

//     if (inputValue && allDependenciesMet && isFocused) {
//       const fetchSuggestions = async () => {
//         try {
//           const response = await axios.get(apiUrl, {
//             headers: getHeaders(),
//             params: {
//               'filters': JSON.stringify(buildFilters(inputValue, ...dependencies))
//             }
//           });
//           setSuggestions(response?.data?.msg?.items || []);
//           setIsOpen(true);
//         } catch (error) {
//           console.error('Error fetching autocomplete data:', error);
//         }
//       };

//       const debounceFetch = setTimeout(() => {
//         fetchSuggestions();
//       }, 0);

//       return () => clearTimeout(debounceFetch);
//     } else {
//       setSuggestions([]);
//       setIsOpen(false);
//     }
//   }, [inputValue, apiUrl, buildFilters, ...dependencies]);

//   const handleSelect = (suggestion) => {
//     justSelected.current = true;
//     const displayValue = suggestion[displayField];
//     setInputValue(displayValue);
//     onSelect(suggestion);
//     setIsOpen(false);
//     // Clear the flag after a short delay to prevent race conditions
//     setTimeout(() => {
//       justSelected.current = false;
//     }, 100);
//   };

//   return (
//     <div className="generic-autocomplete-container">
//       <div className="input-wrapper">
//         <input
//           type="text"
//           className="form-input"
//           placeholder={placeholder}
//           value={inputValue}
//           onChange={(e) => setInputValue(e.target.value)}
//           onFocus={() => {
//             setIsFocused(true);
//             if (justSelected.current) {
//               justSelected.current = false;
//               return;
//             }
//             setIsOpen(true);
//           }}
//           onBlur={(e) => {
//             // Check if the blur is caused by clicking outside the component
//             if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
//               setIsFocused(false);
//               // Close the dropdown with a small delay
//               setTimeout(() => {
//                 if (!justSelected.current) {
//                   setIsOpen(false);
//                 }
//               }, 200);
//             }
//           }}
//         />
//         <FaSearch className="search-icon" />
//       </div>
//       {isOpen && suggestions.length > 0 && (
//         <ul className="suggestions-list">
//           {suggestions.map((suggestion, index) => (
//             <li 
//               key={index}
//               onMouseDown={(e) => {
//                 // Use onMouseDown instead of onClick to ensure it fires before onBlur
//                 e.preventDefault();
//                 handleSelect(suggestion);
//               }}
//             >
//               {suggestion[displayField]}
//             </li>
//           ))}
//         </ul>
//       )}
//     </div>
//   );
// };

// export default GenericAutocomplete;

import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { getHeaders } from "../utils/apiUtils";
import { FaSearch } from "react-icons/fa";
import "./GenericAutocomplete.css";

const GenericAutocomplete = forwardRef(
  (
    {
      onSelect,
      apiUrl,
      buildFilters,
      displayField,
      placeholder,
      value,
      dependencies = [],
    },
    ref
  ) => {
    GenericAutocomplete.displayName = "GenericAutocomplete";
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const justSelected = useRef(false);
    const previousValueRef = useRef(value);

    const [inputValue, setInputValue] = useState(value || "");
    const [suggestions, setSuggestions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    // Expose blur() to parent
    useImperativeHandle(ref, () => ({
      blur: () => inputRef.current?.blur(),
    }));

    // Prefill value - update when value prop changes (including when it becomes empty)
    useEffect(() => {
      // Update if value prop actually changed from parent
      if (value !== previousValueRef.current) {
        previousValueRef.current = value;
        setInputValue(value || '');
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Fetch suggestions
    useEffect(() => {
      const depsReady = dependencies.every((dep) => dep);

      if (inputValue && depsReady && isFocused) {
        const fetchSuggestions = async () => {
          try {
            const response = await axios.get(apiUrl, {
              headers: getHeaders(),
              params: {
                filters: JSON.stringify(
                  buildFilters(inputValue, ...dependencies)
                ),
              },
            });

            setSuggestions(response?.data?.msg?.items || []);
            setIsOpen(true);
          } catch (error) {
            console.error("Error in autocomplete:", error);
          }
        };

        fetchSuggestions();
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, isFocused, apiUrl, buildFilters, ...dependencies]);

    // Select item
    const handleSelect = (suggestion) => {
      justSelected.current = true;

      const displayValue = suggestion[displayField];
      setInputValue(displayValue);
      onSelect(suggestion);

      setIsOpen(false);
      setIsFocused(false);

      // BLUR FIX — prevents reopening
      inputRef.current?.blur();

      setTimeout(() => {
        justSelected.current = false;
      }, 150);
    };

    // Click outside
    useEffect(() => {
      function handleOutside(e) {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          if (!justSelected.current) {
            setIsOpen(false);
            setIsFocused(false);
          }
        }
      }
      document.addEventListener("mousedown", handleOutside);
      return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    return (
      <div className="generic-autocomplete-container" ref={containerRef}>
        <div className="input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="form-input"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              setIsFocused(true);
              setInputValue(e.target.value);
            }}
            onFocus={() => {
              if (!justSelected.current && suggestions.length > 0) {
                setIsOpen(true);
              }
              setIsFocused(true);
            }}
          />
          <FaSearch className="search-icon" />
        </div>

        {isOpen && suggestions.length > 0 && (
          <ul className="suggestions-list">
            {suggestions.map((s, idx) => (
              <li
                key={idx}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(s);
                }}
              >
                {s[displayField]}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

export default GenericAutocomplete;
